import { ProfitLossResponse } from '../../../hooks';

const formatChartMonthLabel = (label: string): string => {
  const [month, year] = label.split(' ');

  if (!month || !year || month.length < 3 || year.length < 2) {
    return label;
  }

  return `${month.slice(0, 3)}' ${year.slice(-2)}`;
};

export type ProfitLossTooltipBreakdown = {
  salesExclVat: number;
  expenses: number;
  surplus: number;
};

export type ProfitLossChartData = {
  xAxisCategories: string[];
  chartSeries: Array<{
    name: string;
    data: number[];
  }>;
  chartColors: Array<'validationGreen' | 'red'>;
  tooltipBreakdown: ProfitLossTooltipBreakdown[];
};

export const getProfitLossChartData = (
  profitLossData: ProfitLossResponse | null,
  selectedMonths: string[],
  monthsForSelectedYear: Record<string, string>,
  chartTitle: string,
): ProfitLossChartData | null => {
  if (!profitLossData?.monthlyBreakdown?.length) return null;

  const monthlyRows =
    selectedMonths.length > 0
      ? profitLossData.monthlyBreakdown.filter((item) => selectedMonths.includes(item.month))
      : profitLossData.monthlyBreakdown;

  const chartRows = monthlyRows.length > 0 ? monthlyRows : profitLossData.monthlyBreakdown;

  const salesExclVatValues = chartRows.map((item) => item.salesExcVat || 0);
  const expenseValues = chartRows.map((item) => item.expenses || 0);
  const derivedNetValues = chartRows.map((item) => (item.salesExcVat || 0) - (item.expenses || 0));
  const surplusValues = chartRows.map((item, index) => item.surplus1 ?? derivedNetValues[index]);

  return {
    xAxisCategories: chartRows.map((item) => {
      const monthLabel = monthsForSelectedYear[item.month] || item.month;

      return formatChartMonthLabel(monthLabel);
    }),
    chartSeries: [
      {
        name: chartTitle,
        data: surplusValues,
      },
    ],
    chartColors: surplusValues.map((value) => (value >= 0 ? 'validationGreen' : 'red')),
    tooltipBreakdown: chartRows.map((_, index) => ({
      salesExclVat: salesExclVatValues[index],
      expenses: expenseValues[index],
      surplus: surplusValues[index],
    })),
  };
};
